import { useState } from 'react';
import GoalsTour from '@/components/tours/GoalsTour';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/contexts/WorkspaceContext';
import {
    Target,
    Plus,
    ChevronDown,
    ChevronRight,
    TrendingUp,
    AlertTriangle,
    CheckCircle2,
    Loader2,
    X,
    Edit3,
    Trash2,
    Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function OKRPage() {
    const { t } = useTranslation();
    usePageTitle('nav.goals');
    const { workspace } = useAuthStore();
    const { canEdit } = usePermissions();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingObjective, setEditingObjective] = useState<any>(null);
    const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set());

    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // Available years: current year - 1, current year, next year
    const availableYears = [currentYear - 1, currentYear, currentYear + 1];

    const { data, isLoading } = useQuery({
        queryKey: ['objectives', workspace?.id, selectedYear],
        queryFn: () => api.getObjectives(workspace!.id, selectedYear),
        enabled: !!workspace?.id,
    });

    const toggleExpanded = (id: string) => {
        const next = new Set(expandedObjectives);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setExpandedObjectives(next);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('goals.title')}</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-neutral-600 dark:text-neutral-400">
                            {t('goals.objectives')}
                        </span>
                        <div className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-700 rounded-lg p-1">
                            {availableYears.map((year) => (
                                <button
                                    key={year}
                                    onClick={() => setSelectedYear(year)}
                                    className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1 ${selectedYear === year
                                        ? 'bg-primary-500 text-white font-medium'
                                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                                        }`}
                                >
                                    {year}
                                    {year < currentYear && (
                                        <span className="text-xs opacity-70">({t('goals.archive', 'Lưu trữ')})</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {canEdit ? (
                    selectedYear < currentYear ? (
                        <div className="flex items-center gap-2 text-sm text-warning-600 bg-warning-50 dark:bg-warning-600/20 px-3 py-2 rounded-lg">
                            <AlertTriangle className="w-4 h-4" />
                            {t('goals.archiveOnly', 'Chế độ xem lưu trữ')}
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary"
                            data-tour="add-objective-btn"
                        >
                            <Plus className="w-4 h-4" />
                            {t('goals.addObjective')}
                        </button>
                    )
                ) : (
                    <div className="flex items-center gap-2 text-sm text-neutral-500 bg-neutral-100 dark:bg-neutral-700 px-3 py-2 rounded-lg">
                        <Lock className="w-4 h-4" />
                        {t('permissions.viewOnly', 'Chế độ chỉ xem')}
                    </div>
                )}
            </div>

            {/* Stats */}
            {data?.stats && (
                <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-600/20 rounded-full">
                        <TrendingUp className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium text-primary-600">
                            {Math.round(data.stats.average_progress * 100)}% {t('goals.avg')}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-success-50 dark:bg-success-600/20 rounded-full">
                        <CheckCircle2 className="w-4 h-4 text-success-600" />
                        <span className="text-sm font-medium text-success-600">
                            {data.stats.on_track} {t('goals.status.onTrack').toLowerCase()}
                        </span>
                    </div>
                    {data.stats.at_risk > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-warning-50 dark:bg-warning-600/20 rounded-full">
                            <AlertTriangle className="w-4 h-4 text-warning-600" />
                            <span className="text-sm font-medium text-warning-600">
                                {data.stats.at_risk} {t('goals.status.atRisk').toLowerCase()}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Objectives List */}
            {!data?.data?.length ? (
                <div className="card p-12 text-center">
                    <Target className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('goals.noObjectives')}</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-6 max-w-md mx-auto">
                        {t('dashboard.createFirst')}
                    </p>
                    {canEdit && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn-primary"
                        >
                            <Plus className="w-4 h-4" />
                            {t('goals.createObjective')}
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {data.data.map((objective) => (
                        <ObjectiveCard
                            key={objective.id}
                            objective={objective}
                            expanded={expandedObjectives.has(objective.id)}
                            onToggle={() => toggleExpanded(objective.id)}
                            onEdit={() => setEditingObjective(objective)}
                        />
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <ObjectiveModal
                    workspaceId={workspace?.id || ''}
                    onClose={() => setShowCreateModal(false)}
                    onYearChange={setSelectedYear}
                />
            )}

            {/* Edit Modal */}
            {editingObjective && (
                <ObjectiveModal
                    workspaceId={workspace?.id || ''}
                    objective={editingObjective}
                    onClose={() => setEditingObjective(null)}
                    onYearChange={setSelectedYear}
                />
            )}

            {/* Onboarding Tour */}
            <GoalsTour />
        </div>
    );
}

function ObjectiveCard({
    objective,
    expanded,
    onToggle,
    onEdit,
}: {
    objective: any;
    expanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
}) {
    const { t } = useTranslation();
    const [showAddKR, setShowAddKR] = useState(false);
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: () => api.deleteObjective(objective.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objectives'] });
        },
    });

    const statusColors = {
        on_track: 'bg-success-500',
        at_risk: 'bg-warning-500',
        off_track: 'bg-danger-500',
        not_started: 'bg-neutral-400',
        completed: 'bg-primary-500',
    };

    return (
        <div className="card overflow-hidden">
            {/* Header */}
            <div className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                <div className="flex items-start gap-3">
                    <button className="mt-1 text-neutral-400" onClick={onToggle}>
                        {expanded ? (
                            <ChevronDown className="w-5 h-5" />
                        ) : (
                            <ChevronRight className="w-5 h-5" />
                        )}
                    </button>

                    <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggle}>
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold truncate">{objective.title}</h3>
                            <span className="badge-neutral text-xs">{objective.period_type}</span>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-neutral-500">
                            <span>{objective.key_results?.length || 0} {t('goals.keyResults')}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                            <div className="text-2xl font-bold text-primary-600">
                                {Math.round(objective.progress * 100)}%
                            </div>
                            <div className="flex items-center gap-1 justify-end mt-1">
                                <div className={clsx('w-2 h-2 rounded-full', statusColors[objective.status as keyof typeof statusColors] || 'bg-neutral-400')} />
                                <span className="text-xs text-neutral-500 capitalize">
                                    {objective.status?.replace('_', ' ')}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                        >
                            <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(t('goals.deleteObjective') + '?')) deleteMutation.mutate();
                            }}
                            className="p-1.5 text-neutral-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3 ml-8 progress-bar">
                    <div
                        className="progress-bar-fill bg-primary-500"
                        style={{ width: `${Math.round(objective.progress * 100)}%` }}
                    />
                </div>
            </div>

            {/* Key Results */}
            {expanded && (
                <div className="border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50">
                    {objective.key_results?.map((kr: any, idx: number) => (
                        <KeyResultRow key={kr.id} keyResult={kr} objectiveId={objective.id} isLast={idx === objective.key_results.length - 1 && !showAddKR} />
                    ))}

                    {/* Add Key Result Button / Form */}
                    <div className="px-4 py-3 ml-8">
                        {showAddKR ? (
                            <AddKeyResultForm
                                objectiveId={objective.id}
                                onClose={() => setShowAddKR(false)}
                            />
                        ) : (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddKR(true);
                                }}
                                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                            >
                                <Plus className="w-4 h-4" />
                                {t('goals.addKeyResult')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function AddKeyResultForm({ objectiveId, onClose }: { objectiveId: string; onClose: () => void }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Get default dates (current quarter)
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const quarterStart = new Date(currentYear, Math.floor(currentMonth / 3) * 3, 1);
    const quarterEnd = new Date(currentYear, Math.floor(currentMonth / 3) * 3 + 3, 0);

    const [formData, setFormData] = useState({
        title: '',
        start_date: quarterStart.toISOString().split('T')[0],
        end_date: quarterEnd.toISOString().split('T')[0],
    });

    const createMutation = useMutation({
        mutationFn: () => api.createKeyResult(objectiveId, {
            title: formData.title,
            metric_type: 'task_completion',
            start_value: 0,
            target_value: 100,
            start_date: formData.start_date,
            end_date: formData.end_date,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objectives'] });
            onClose();
        },
    });

    return (
        <div className="space-y-3 p-3 bg-white dark:bg-neutral-700 rounded-lg border border-neutral-200 dark:border-neutral-600">
            <div>
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input text-sm"
                    placeholder={t('goals.krTitlePlaceholder', 'VD: Hoàn thành 10 tính năng mới')}
                    autoFocus
                />
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-neutral-500">{t('goals.startDate', 'Bắt đầu')}</label>
                    <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="input text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs text-neutral-500">{t('goals.endDate', 'Kết thúc')}</label>
                    <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="input text-sm"
                        min={formData.start_date}
                    />
                </div>
            </div>

            {/* Info about task-based progress */}
            <div className="text-xs text-neutral-500 bg-neutral-50 dark:bg-neutral-600/50 p-2 rounded">
                💡 {t('goals.krTaskBasedInfo', 'Tiến độ sẽ được tính tự động dựa trên các task liên kết đã hoàn thành.')}
            </div>

            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-ghost text-xs px-3 py-1.5">
                    {t('common.cancel')}
                </button>
                <button
                    onClick={() => createMutation.mutate()}
                    disabled={createMutation.isPending || !formData.title}
                    className="btn-primary text-xs px-3 py-1.5"
                >
                    {createMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('common.add')}
                </button>
            </div>
        </div>
    );
}


function KeyResultRow({ keyResult, objectiveId: _objectiveId, isLast }: { keyResult: any; objectiveId: string; isLast: boolean }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: () => api.deleteKeyResult(keyResult.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objectives'] });
        },
    });

    const statusColors = {
        on_track: 'text-success-600 bg-success-50',
        at_risk: 'text-warning-600 bg-warning-50',
        off_track: 'text-danger-600 bg-danger-50',
        not_started: 'text-neutral-600 bg-neutral-100',
        completed: 'text-primary-600 bg-primary-50',
    };

    // Calculate linked tasks info from keyResult data if available
    const linkedTasksCount = keyResult.linked_tasks_count || 0;
    const completedTasksCount = keyResult.completed_tasks_count || 0;

    return (
        <div className={clsx('px-4 py-3 ml-8', !isLast && 'border-b border-neutral-200 dark:border-neutral-700')}>
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{keyResult.title}</p>

                    {/* Clear progress explanation */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500">
                        {/* Progress display */}
                        <div className="flex items-center gap-2">
                            <div className="w-20 progress-bar">
                                <div
                                    className="progress-bar-fill bg-primary-500"
                                    style={{ width: `${Math.round(keyResult.progress * 100)}%` }}
                                />
                            </div>
                            <span className="font-medium text-primary-600">
                                {Math.round(keyResult.progress * 100)}%
                            </span>
                        </div>

                        {/* Status badge */}
                        <span
                            className={clsx(
                                'px-2 py-0.5 rounded-full',
                                statusColors[keyResult.status as keyof typeof statusColors] || statusColors.not_started
                            )}
                        >
                            {keyResult.status?.replace('_', ' ')}
                        </span>
                    </div>

                    {/* Linked tasks info - THE KEY IMPROVEMENT */}
                    <div className="mt-2 p-2 bg-primary-50/50 dark:bg-primary-900/20 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs">
                                <Target className="w-3.5 h-3.5 text-primary-600" />
                                <span className="text-neutral-600 dark:text-neutral-400">
                                    {t('goals.krLinkedTasks')}:
                                </span>
                                {linkedTasksCount > 0 ? (
                                    <span className="font-medium text-primary-600">
                                        {completedTasksCount}/{linkedTasksCount} {t('common.tasks')}
                                    </span>
                                ) : (
                                    <span className="text-warning-600">
                                        {t('goals.krNoTasks')}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* How it works tooltip */}
                        <p className="text-xs text-neutral-500 mt-1">
                            {t('goals.krHowItWorks')}
                        </p>
                    </div>

                    {/* Value display - show current/target for transparency */}
                    <div className="flex items-center gap-2 mt-2 text-xs text-neutral-400">
                        <span>
                            {keyResult.current_value} / {keyResult.target_value} {keyResult.unit || ''}
                        </span>
                        <span className="text-neutral-300">•</span>
                        <span className="italic">{t('goals.krProgressAuto')}</span>
                    </div>
                </div>

                {/* Actions - only delete, no manual check-in */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => { if (confirm(t('common.delete') + ' Key Result?')) deleteMutation.mutate(); }}
                        className="p-1.5 text-neutral-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                        title={t('common.delete')}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function ObjectiveModal({
    workspaceId,
    objective,
    onClose,
    onYearChange,
}: {
    workspaceId: string;
    objective?: any;
    onClose: () => void;
    onYearChange?: (year: number) => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const isEdit = !!objective;
    const currentYear = new Date().getFullYear();

    // Date type: 'quarter' or 'custom'
    const [dateType, setDateType] = useState<'quarter' | 'custom'>(
        objective?.period_type === 'CUSTOM' ? 'custom' : 'quarter'
    );

    const [formData, setFormData] = useState({
        title: objective?.title || '',
        description: objective?.description || '',
        period_type: objective?.period_type || 'Q4',
        year: objective?.year || currentYear,
        start_date: objective?.start_date?.split('T')[0] || '',
        end_date: objective?.end_date?.split('T')[0] || '',
        status: objective?.status || 'active',
    });

    const getQuarterDates = (quarter: string, year: number): [string, string] => {
        const quarters: Record<string, [string, string]> = {
            Q1: [`${year}-01-01`, `${year}-03-31`],
            Q2: [`${year}-04-01`, `${year}-06-30`],
            Q3: [`${year}-07-01`, `${year}-09-30`],
            Q4: [`${year}-10-01`, `${year}-12-31`],
            ANNUAL: [`${year}-01-01`, `${year}-12-31`],
        };
        return quarters[quarter] || quarters.Q4;
    };

    const createMutation = useMutation({
        mutationFn: () => {
            let start: string, end: string;
            if (dateType === 'custom') {
                start = formData.start_date;
                end = formData.end_date;
            } else {
                [start, end] = getQuarterDates(formData.period_type, formData.year);
            }
            return api.createObjective({
                workspace_id: workspaceId,
                title: formData.title,
                description: formData.description || undefined,
                period_type: dateType === 'custom' ? 'CUSTOM' : formData.period_type,
                year: formData.year,
                start_date: start,
                end_date: end,
                status: formData.status as any,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objectives'] });
            // Auto-switch to the year where objective was created
            if (onYearChange) {
                onYearChange(formData.year);
            }
            onClose();
        },
    });

    const updateMutation = useMutation({
        mutationFn: () => {
            return api.updateObjective(objective.id, {
                title: formData.title,
                description: formData.description || undefined,
                status: formData.status as any,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objectives'] });
            onClose();
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isEdit) {
            updateMutation.mutate();
        } else {
            createMutation.mutate();
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    // Available years: current and next year only (can't create for past years)
    const availableYears = isEdit
        ? [currentYear - 1, currentYear, currentYear + 1] // Edit can modify past year objectives
        : [currentYear, currentYear + 1]; // Create only for current and next year

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-lg animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold">
                        {isEdit ? t('goals.editObjective', 'Sửa mục tiêu') : t('goals.createObjective', 'Tạo mục tiêu')}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div>
                        <label className="label">{t('goals.objectiveTitle', 'Tiêu đề')}</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="input"
                            placeholder={t('goals.titlePlaceholder', 'VD: Ra mắt sản phẩm MVP')}
                            required
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="label">{t('goals.objectiveDescription', 'Mô tả')} ({t('common.optional', 'tùy chọn')})</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="input min-h-[80px]"
                            placeholder={t('goals.descriptionPlaceholder', 'Thành công sẽ như thế nào?')}
                        />
                    </div>

                    {/* Status Selector */}
                    <div>
                        <label className="label">{t('goals.status', 'Trạng thái')}</label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="input"
                        >
                            <option value="draft">📝 {t('goals.status.draft', 'Nháp')}</option>
                            <option value="active">▶️ {t('goals.status.active', 'Đang chạy')}</option>
                            <option value="completed">✅ {t('goals.status.completed', 'Hoàn thành')}</option>
                            <option value="cancelled">❌ {t('goals.status.cancelled', 'Đã hủy')}</option>
                        </select>
                        <p className="text-xs text-neutral-500 mt-1">
                            {formData.status === 'draft' && t('goals.statusHelp.draft', 'Mục tiêu đang được soạn thảo, chưa chính thức')}
                            {formData.status === 'active' && t('goals.statusHelp.active', 'Mục tiêu đang được theo dõi và thực hiện')}
                            {formData.status === 'completed' && t('goals.statusHelp.completed', 'Mục tiêu đã đạt được')}
                            {formData.status === 'cancelled' && t('goals.statusHelp.cancelled', 'Mục tiêu không còn phù hợp')}
                        </p>
                    </div>

                    {/* Date Type Toggle */}
                    <div>
                        <label className="label">{t('goals.dateType', 'Loại thời hạn')}</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setDateType('quarter')}
                                className={clsx(
                                    'flex-1 px-3 py-2 text-sm rounded-lg border transition-colors',
                                    dateType === 'quarter'
                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-600/20 text-primary-600'
                                        : 'border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                                )}
                            >
                                {t('goals.byQuarter', 'Theo quý')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDateType('custom')}
                                className={clsx(
                                    'flex-1 px-3 py-2 text-sm rounded-lg border transition-colors',
                                    dateType === 'custom'
                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-600/20 text-primary-600'
                                        : 'border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                                )}
                            >
                                {t('goals.byDate', 'Theo ngày')}
                            </button>
                        </div>
                    </div>

                    {dateType === 'quarter' ? (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label">{t('goals.periodType', 'Kỳ hạn')}</label>
                                <select
                                    value={formData.period_type}
                                    onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                                    className="input"
                                >
                                    <option value="Q1">{t('goals.periods.Q1', 'Quý 1')}</option>
                                    <option value="Q2">{t('goals.periods.Q2', 'Quý 2')}</option>
                                    <option value="Q3">{t('goals.periods.Q3', 'Quý 3')}</option>
                                    <option value="Q4">{t('goals.periods.Q4', 'Quý 4')}</option>
                                    <option value="ANNUAL">{t('goals.periods.ANNUAL', 'Cả năm')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="label">{t('goals.year', 'Năm')}</label>
                                <select
                                    value={formData.year}
                                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                    className="input"
                                >
                                    {availableYears.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label">{t('goals.startDate', 'Ngày bắt đầu')}</label>
                                <input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => {
                                        const date = new Date(e.target.value);
                                        setFormData({
                                            ...formData,
                                            start_date: e.target.value,
                                            year: date.getFullYear()
                                        });
                                    }}
                                    className="input"
                                    required={dateType === 'custom'}
                                />
                            </div>
                            <div>
                                <label className="label">{t('goals.endDate', 'Ngày kết thúc')}</label>
                                <input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    className="input"
                                    min={formData.start_date}
                                    required={dateType === 'custom'}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            {t('common.cancel', 'Hủy')}
                        </button>
                        <button type="submit" disabled={isPending} className="btn-primary">
                            {isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    {isEdit ? <Edit3 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    {isEdit ? t('common.save', 'Lưu') : t('common.create', 'Tạo')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
